<script setup lang="ts">
/**
 * The reminder the app writes and the organizer sends (SPEC §6.23, §6.23.1).
 *
 * MVP-Family sends nothing. This works out who still needs reminding, writes
 * the message on the server from the templates the automatic sender will use,
 * and hands it to the organizer for their family's existing group text.
 *
 * The audience is shown by name as well as by count, because "4 households" is
 * not something anybody can check and a reminder sent to the wrong set cannot
 * be recalled.
 *
 * Two ways to send, side by side (MT-162). Email goes to each household's
 * confirmed addresses, one message each with their own link, and is queued
 * rather than sent while the dialog waits. Copy is still there for the family
 * group text. Before either, the dialog says how many people email would
 * reach and how many it would not — silence is not delivery, and an organizer
 * who assumes it is will not chase the three families nobody could email.
 */
import { computed, ref, watch } from 'vue'
import { ApiError, endpoints, type QueuedReminderResponse, type ReminderDraftResponse } from '@/api'
import { asCount } from '@/utils/numbers'

const props = defineProps<{
  groupId: string
  eventId: string
  /** Loads only while this is true, so a closed dialog costs nothing. */
  open: boolean
}>()

const emit = defineEmits<{ sent: [] }>()

/** SPEC §6.23's list, in the order an organizer reaches for them. */
const AUDIENCES = [
  { value: 'Unanswered', label: "Haven't answered", kind: 'Rsvp' },
  { value: 'Maybe', label: 'Said maybe', kind: 'Rsvp' },
  { value: 'NotContributing', label: 'Not bringing anything', kind: 'StillNeeded' },
  { value: 'Contributing', label: 'Bringing something', kind: 'Contribution' },
  { value: 'Everyone', label: 'Everybody invited', kind: 'Rsvp' },
] as const

type AudienceValue = (typeof AUDIENCES)[number]['value']

const audience = ref<AudienceValue>('Unanswered')

const draft = ref<ReminderDraftResponse | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

const copied = ref(false)
const recording = ref(false)

const queuing = ref(false)
const queued = ref<QueuedReminderResponse | null>(null)
const queueError = ref<string | null>(null)

const emailPeople = computed(() => asCount(draft.value?.emailPeople))
const emailNotReached = computed(() => asCount(draft.value?.emailNotReached))
const householdsNotReached = computed(() => asCount(draft.value?.householdsNotReached))

/** What just happened, for anybody not watching the button. */
const announcement = ref('')

/** The message this audience is about, which the server decides from the kind. */
const kind = computed(
  () => AUDIENCES.find((option) => option.value === audience.value)?.kind ?? 'Rsvp',
)

const nobody = computed(() => draft.value !== null && draft.value.householdCount === 0)

/**
 * `sms:?&body=` rather than either platform-specific spelling. iOS wants
 * `&body=` and Android wants `?body=`, and this form is the one both have
 * accepted for years.
 */
const smsHref = computed(() =>
  draft.value ? `sms:?&body=${encodeURIComponent(draft.value.message)}` : '',
)

/** Only where the browser has a share sheet. Feature-detected, never assumed. */
const canShare = computed(
  () => typeof navigator !== 'undefined' && typeof navigator.share === 'function',
)

function describe(caught: unknown): string {
  return (
    (caught instanceof ApiError && caught.problem?.title) ||
    'We could not work out who to remind just then. Try again in a moment.'
  )
}

async function load() {
  loading.value = true
  error.value = null
  copied.value = false
  queued.value = null
  queueError.value = null

  try {
    draft.value = await endpoints.draftEventReminder(props.groupId, props.eventId, {
      audience: audience.value,
      kind: kind.value,
    })
  } catch (caught) {
    error.value = describe(caught)
    draft.value = null
  } finally {
    loading.value = false
  }
}

/**
 * Copies the message and records that it went out.
 *
 * Copying is what sending means here, so it is recorded (SPEC §6.23.1). The
 * record is a claim rather than a receipt — the organizer may copy and then not
 * paste — which is exactly why it is stored as `Manual` and not as a delivery.
 *
 * A failed recording does not undo the copy. The message is on the clipboard
 * either way, and telling somebody their reminder did not send when it is
 * sitting in their paste buffer would be false.
 */
/**
 * Queues the reminder to be emailed (MT-162).
 *
 * Queued, not sent: the answer is immediate and says so, and the messages go
 * out on the worker's next pass. The history entry is written by the sender
 * once they have gone, not here.
 */
async function email() {
  if (!draft.value) return

  queuing.value = true
  queueError.value = null

  try {
    queued.value = await endpoints.queueEmailReminder(props.groupId, props.eventId, {
      audience: audience.value,
      kind: kind.value,
    })
    announcement.value = `Queued for ${asCount(queued.value.people)} people. They will go out in the next few minutes.`
    emit('sent')
  } catch (caught) {
    queueError.value =
      (caught instanceof ApiError &&
        (Object.values(caught.problem?.errors ?? {})[0]?.[0] ?? caught.problem?.title)) ||
      'That did not queue. Try again in a moment.'
  } finally {
    queuing.value = false
  }
}

async function copy() {
  if (!draft.value) return

  try {
    await navigator.clipboard.writeText(draft.value.message)
    copied.value = true
    announcement.value = 'Message copied. Paste it into your family group text.'
  } catch {
    // A browser that refuses clipboard access is not an error worth showing:
    // the message is on screen and selectable.
    copied.value = false
    return
  }

  recording.value = true

  try {
    await endpoints.recordEventReminder(props.groupId, props.eventId, {
      audience: audience.value,
      kind: kind.value,
      method: 'Manual',
    })

    emit('sent')
  } catch {
    // Deliberately quiet. The reminder history being one entry short is a
    // smaller problem than an error message about a message that did copy.
  } finally {
    recording.value = false
  }
}

async function hand() {
  if (!draft.value) return

  try {
    await navigator.share({ text: draft.value.message })
  } catch {
    // Includes the visitor closing the sheet, which is not a failure.
  }
}

watch(
  [() => props.open, audience],
  ([isOpen]) => {
    if (isOpen) void load()
  },
  // Immediate, because the dialog that holds this is usually already open by
  // the time the component mounts. Without it the panel renders empty and
  // waits for an audience change that nobody is going to make.
  { immediate: true },
)
</script>

<template>
  <section class="remind">
    <p aria-live="polite" class="remind__announcement">{{ announcement }}</p>

    <v-alert v-if="error" type="error" variant="tonal">{{ error }}</v-alert>

    <v-select
      v-model="audience"
      :items="[...AUDIENCES]"
      item-title="label"
      item-value="value"
      label="Who needs reminding"
      variant="outlined"
      density="comfortable"
      hide-details
      data-testid="reminder-audience"
    />

    <v-skeleton-loader v-if="loading" type="paragraph" />

    <template v-else-if="draft">
      <!--
        Names as well as a count. "4 households" is not something an organizer
        can check, and a reminder sent to the wrong set cannot be recalled
        (SPEC §6.23.1).
      -->
      <div class="remind__audience" data-testid="reminder-audience-summary">
        <p class="remind__count">
          {{ draft.householdCount }}
          {{ draft.householdCount === 1 ? 'household' : 'households' }}
        </p>
        <p v-if="draft.households.length > 0" class="remind__names">
          {{ draft.households.join(' · ') }}
        </p>
      </div>

      <p v-if="nobody" class="remind__nobody" data-testid="reminder-nobody">
        Nobody is in this group right now. There is nothing to send.
      </p>

      <template v-else>
        <!--
          Selectable, and looking like something to be copied rather than read,
          so a browser that refuses clipboard access still leaves a way through.
        -->
        <p class="remind__message" data-testid="reminder-message">{{ draft.message }}</p>

        <p class="remind__cost">
          {{ draft.message.length }} characters · {{ draft.segments }}
          {{ draft.segments === 1 ? 'text' : 'texts' }} if you send it as one
        </p>

        <!--
          Who email reaches, before anybody presses anything (MT-162). The
          not-reached count is the point: those families hear nothing unless
          somebody tells them another way.
        -->
        <p class="remind__reach" data-testid="reminder-email-reach">
          <template v-if="emailPeople > 0">
            Email reaches {{ emailPeople }} {{ emailPeople === 1 ? 'person' : 'people' }}
          </template>
          <template v-else>Nobody here has a confirmed email address</template>
          <template v-if="householdsNotReached > 0">
            · {{ householdsNotReached }}
            {{ householdsNotReached === 1 ? 'household' : 'households' }} can't be emailed
          </template>
          <template v-else-if="emailNotReached > 0">
            · {{ emailNotReached }} {{ emailNotReached === 1 ? 'person' : 'people' }} can't be
            emailed
          </template>
        </p>

        <p v-if="queued" class="remind__queued" role="status" data-testid="reminder-queued">
          Queued for {{ asCount(queued.people) }}
          {{ asCount(queued.people) === 1 ? 'person' : 'people' }}. They'll go out in the next few
          minutes.
        </p>
        <p v-if="queueError" class="remind__error" role="alert">{{ queueError }}</p>

        <div class="remind__actions">
          <v-btn
            v-if="emailPeople > 0"
            class="remind__action"
            color="primary"
            variant="flat"
            :loading="queuing"
            :disabled="queued !== null"
            data-testid="reminder-email"
            @click="email"
          >
            {{
              queued ? 'Queued' : `Email ${emailPeople} ${emailPeople === 1 ? 'person' : 'people'}`
            }}
          </v-btn>

          <v-btn
            class="remind__action"
            :color="emailPeople > 0 ? undefined : 'primary'"
            :variant="emailPeople > 0 ? 'outlined' : 'flat'"
            :loading="recording"
            @click="copy"
          >
            {{ copied ? 'Copied' : 'Copy' }}
          </v-btn>

          <v-btn v-if="canShare" class="remind__action" variant="outlined" @click="hand">
            Share
          </v-btn>

          <!--
            An anchor rather than a button. sms: is handled by the operating
            system, and a click handler that set location would be a click
            handler doing what a link already does.
          -->
          <v-btn
            class="remind__action"
            variant="outlined"
            :href="smsHref"
            data-testid="reminder-sms"
          >
            Open Messages
          </v-btn>
        </div>
      </template>
    </template>
  </section>
</template>

<style scoped>
.remind__reach,
.remind__queued {
  margin: 0;
  font-size: 0.875rem;
}

.remind__queued {
  color: rgb(var(--v-theme-success));
  font-weight: 500;
}

.remind__error {
  margin: 0;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-error));
}

.remind {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.remind__announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.remind__audience {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.remind__count {
  margin: 0;
  font-weight: 600;
}

.remind__names,
.remind__cost {
  margin: 0;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-on-surface-variant));
}

.remind__nobody {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
}

.remind__message {
  margin: 0;
  padding: 0.75rem;
  border: 1px solid rgb(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  user-select: all;
}

.remind__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Thumb-sized, like every other target in this product (SPEC §9.5). */
.remind__action {
  min-height: 2.75rem;
  flex: 1 1 8rem;
}
</style>
